import { useDialogStore } from '@ariakit/react';
import { parseDateValue } from '@ben/common/date';
import Checkbox from '@ben/orion/checkbox';
import DateInput from '@ben/orion/date-input';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import Table, { type TableColumn } from '@ben/orion/table';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IRateTableData } from '@ben/shared/types/columbia';
import { exportCsvFile } from '@ben/shared/utils/export-csv-file';
import { snakeCase } from 'lodash';
import Papa from 'papaparse';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import { useSetDirtyValue } from '../hooks/use-set-dirty-value';
import DeleteConfirmationModal from './delete-confirmation-modal';
import FallbackConfirmationModal from './fallback-confirmation-modal';
import RenameTableConfirmationModal from './rename-table-confirmation-modal';

interface ITableContainerProps {
  tableName: string;
  rowData: IRateTableData[];
  id?: number;
  index: number;
  isFallback: boolean;
  handleDeleteTable: () => void;
  handleReplaceCSV: () => void;
  effectiveFrom?: string;
}

const TableContainer: FC<ITableContainerProps> = ({
  tableName: incomingTableName,
  rowData,
  id,
  index,
  isFallback: incomingIsFallback,
  handleDeleteTable,
  handleReplaceCSV,
  effectiveFrom: incomingEffectiveFrom,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableNames, setTableNames] = useState<string[]>([]);
  const [tableName, setTableName] = useState(incomingTableName);
  const [isFallback, setIsFallback] = useState(incomingIsFallback);
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);
  const [isDuplicateTableName, setIsDuplicateTableName] = useState(false);
  const [isEmptyEffectiveFrom, setIsEmptyEffectiveFrom] = useState(false);
  const [isEmptyTableName, setIsEmptyTableName] = useState(false);
  const [reRenderKey, setReRenderKey] = useState(0);
  const [effectiveFrom, setEffectiveFrom] = useState(incomingEffectiveFrom);

  useEffect(() => {
    setIsFallback(incomingIsFallback);
    setEffectiveFrom(incomingEffectiveFrom);
  }, [incomingIsFallback, incomingEffectiveFrom]);

  const deleteConfirmationDialog = useDialogStore();
  const renameTableConfirmationDialog = useDialogStore();

  const setDirtyValue = useSetDirtyValue();
  const rateTables = useWatch({
    name: 'additionalConfig.rateTables',
    exact: true,
  });
  const subPrices = useWatch({
    name: 'additionalConfig.subPrices',
    exact: true,
  });

  const handleRenameRateTable = useCallback(
    ({
      index,
      key,
      isFallback,
      effectiveFrom,
    }: {
      index: number;
      key: string;
      isFallback: boolean;
      effectiveFrom?: string;
    }) => {
      const newRateTables = rateTables.map(
        (
          table: { key: string; isFallback: boolean; effectiveFrom?: string },
          index_: number
        ) => {
          if (index_ === index) {
            return {
              ...table,
              key,
              isFallback,
              effectiveFrom,
            };
          }
          return table;
        }
      );
      setDirtyValue('additionalConfig.rateTables', newRateTables);
    },
    [rateTables, setDirtyValue]
  );

  // Get's current fallback table, unless it's itself
  const currentFallbackTable = useMemo(() => {
    const fallbackIndex = rateTables.findIndex(
      (table: { isFallback: boolean }) => table.isFallback
    );

    return fallbackIndex !== undefined && fallbackIndex !== index
      ? rateTables[fallbackIndex]
      : undefined;
  }, [rateTables, index]);

  const benefitPriceFormulas = useMemo(() => {
    return subPrices?.map(
      (subPrice: { pricingFormula?: string }) =>
        subPrice.pricingFormula?.match(/RATE\("([^"]+)"\)/)?.[1] || ''
    );
  }, [subPrices]);

  useEffect(() => {
    if (!rateTables) return;

    const tableNames_ = rateTables.map((table: { key: string }) => table.key);
    setTableNames(tableNames_);
  }, [rateTables]);

  const columns = useMemo((): TableColumn<object>[] | undefined => {
    if (!rowData[0]) {
      return;
    }
    return Object.keys(rowData[0]).map((header) => ({
      header: snakeCase(header),
      accessorKey: header,
    }));
  }, [rowData]);

  const handleDelete = useCallback(() => {
    deleteConfirmationDialog.show();
  }, [deleteConfirmationDialog]);

  const handleFallbackConfirmation = () => {
    // User accepted to replace fallback table, so removing isFallback from other tables
    for (const [index, table] of rateTables.entries()) {
      if (table.isFallback) {
        handleRenameRateTable({
          index: index,
          key: table.key,
          isFallback: false,
          effectiveFrom: table.effectiveFrom,
        });
      }
    }

    handleRenameRateTable({
      index,
      key: tableName,
      isFallback,
      effectiveFrom,
    });

    setIsFallbackModalOpen(false);
    setIsEditMode(false);
  };

  const handleRename = () => {
    if (!tableName || !effectiveFrom) {
      setIsEmptyTableName(!tableName);
      setIsEmptyEffectiveFrom(!effectiveFrom);
      return;
    }

    const isRateTableNameReferencedInFormula =
      benefitPriceFormulas?.includes(incomingTableName);

    // If the user tries to save but there are conflicting fallback tables bring up fallback confirmation
    if (isFallback && currentFallbackTable) {
      setIsFallbackModalOpen(true);
      return;
    }

    if (incomingTableName !== tableName && isRateTableNameReferencedInFormula) {
      renameTableConfirmationDialog.show();
    } else {
      setIsEmptyTableName(false);
      setIsEmptyEffectiveFrom(false);
      setIsEditMode(false);
      handleRenameRateTable({
        index,
        key: tableName,
        isFallback,
        effectiveFrom,
      });
    }
  };

  const handleRenameTable = useCallback(() => {
    const tableNamesExcludingCurrentIndex = tableNames?.filter(
      (_, index_) => index_ !== index
    );
    if (tableNamesExcludingCurrentIndex?.includes(tableName)) {
      setIsDuplicateTableName(true);
      setIsEditMode(true);
      return;
    } else {
      setIsDuplicateTableName(false);
      setIsEditMode(false);
      handleRenameRateTable({
        index,
        key: tableName,
        isFallback,
        effectiveFrom,
      });
      toast.success('Table renamed, please save to confirm changes');
    }
    renameTableConfirmationDialog.hide();
  }, [
    tableNames,
    tableName,
    renameTableConfirmationDialog,
    handleRenameRateTable,
    index,
    isFallback,
    effectiveFrom,
  ]);

  const handleDownloadAsCSV = useCallback(() => {
    exportCsvFile({
      data: Papa.unparse(rowData),
      fileName: tableName,
    });
  }, [rowData, tableName]);

  return (
    <Container>
      <ExpandCollapseContainer>
        <InlineTextButton
          label={isTableExpanded ? '- Collapse Table' : '+ Expand Table'}
          onClick={() => {
            setIsTableExpanded(!isTableExpanded);
          }}
        />
      </ExpandCollapseContainer>
      <HeaderContainer>
        <HeaderSectionsContainer>
          <Input
            placeholder="Table Name"
            name="Rate Table Name"
            value={tableName}
            onChange={(event) => {
              setTableName(event.target.value);
              setIsEmptyTableName(false);
            }}
            disabled={!isEditMode}
            hasError={isDuplicateTableName || isEmptyTableName}
            errorMessage={
              isDuplicateTableName
                ? 'Duplicate file name.'
                : fieldCannotBeBlankErrorMessage
            }
            key={reRenderKey}
          />
          <StyledInlineTextButton
            label={isEditMode ? 'Save' : 'Edit'}
            onClick={() => (isEditMode ? handleRename() : setIsEditMode(true))}
          />
        </HeaderSectionsContainer>
        <HeaderSectionsActionContainer>
          {!id && (
            <Button
              variant={ButtonVariant.secondary}
              iconName={IconName.Upload}
              label="Replace"
              onClick={handleReplaceCSV}
              width="120px"
            />
          )}
          <Button
            variant={ButtonVariant.secondary}
            iconName={IconName.Download}
            label="Download .csv"
            onClick={handleDownloadAsCSV}
            width="185px"
          />
          <Button
            variant={ButtonVariant.secondary}
            iconName={IconName.Trash}
            label="Delete"
            onClick={handleDelete}
            width="120px"
          />
        </HeaderSectionsActionContainer>
      </HeaderContainer>
      {isTableExpanded && (
        <ContentContainer>
          <DateInput
            label="Effective from"
            width="100%"
            disabled={!isEditMode}
            onChange={(date) => {
              const next = date
                ? date.toISOString().replace(/\.(\d{3})Z$/, '.$1000Z')
                : '';
              setEffectiveFrom(next);
              setIsEmptyEffectiveFrom(false);
              handleRenameRateTable({
                index,
                key: tableName,
                isFallback,
                effectiveFrom: next,
              });
            }}
            value={effectiveFrom ? parseDateValue(effectiveFrom) : null}
            errorMessage={
              isEmptyEffectiveFrom ? fieldCannotBeBlankErrorMessage : undefined
            }
          />
          {columns ? (
            <Table columns={columns} data={rowData} ariaLabel="Rate table" />
          ) : (
            <Typography variant={TypographyVariant.bodyMd2}>
              The data for this table does not exist
            </Typography>
          )}
          <Checkbox
            label="Fallback table"
            showLabel
            value={isFallback}
            onChange={(value: boolean) => {
              setIsFallback(value);
            }}
            disabled={!isEditMode}
          />
        </ContentContainer>
      )}
      <DeleteConfirmationModal
        dialog={deleteConfirmationDialog}
        filename={tableName}
        handleDeleteTable={handleDeleteTable}
      />
      <RenameTableConfirmationModal
        dialog={renameTableConfirmationDialog}
        filename={tableName}
        handleSaveRename={handleRenameTable}
        incomingTableName={incomingTableName}
        setTableName={setTableName}
        setReRenderKey={setReRenderKey}
      />

      {currentFallbackTable ? (
        <FallbackConfirmationModal
          isOpen={isFallbackModalOpen}
          onClose={() => setIsFallbackModalOpen(false)}
          onConfirm={handleFallbackConfirmation}
        />
      ) : (
        false
      )}
    </Container>
  );
};

export default memo(TableContainer);

const Container = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(3)};
    background-color: ${theme.palette.primary.bg};
    border-radius: ${theme.borderRadius.sm};
  `}
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const HeaderSectionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
    align-items: center;
  `}
`;

const HeaderSectionsActionContainer = styled(HeaderSectionsContainer)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(3.5)};
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(3)};
  `}
`;

const ExpandCollapseContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(3)};
    flex-direction: column;
  `}
`;
